import './Applicability.css';

import React, { useEffect, useState } from 'react';
import { Checkbox } from '@mui/material';
import { generateClassName } from '../../../b2b-common/helpers';
import SelectableList from '../../../b2b-common/SelectableList';
import FieldComponent from '../../../b2b-common/FieldComponent';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import useB2BTranslation from '../../../b2b-common/hooks/useB2BTranslation';

const hours = Array.from({ length: 24 }, (_, i) => ({
    label: i.toString().padStart(2, '0'),
    value: i
}));

const Applicability = ({ objective, setObjective, edit = false, className = null }) => {
    const { t } = useB2BTranslation('b2b-plans');
    const [currentObjective, setCurrentObjective] = useState(objective);
    const [enableApplicability, setEnableApplicability] = useState(false);
    const [cronExpression, setCronExpression] = useState(null);
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [selectedDays, setSelectedDays] = useState([]);
    const [selectedHours, setSelectedHours] = useState([]);
    
    const months = [
        { label: t('January'), value: 'JAN' },
        { label: t('February'), value: 'FEB' },
        { label: t('March'), value: 'MAR' },
        { label: t('April'), value: 'APR' },
        { label: t('May'), value: 'MAY' },
        { label: t('June'), value: 'JUN' },
        { label: t('July'), value: 'JUL' },
        { label: t('August'), value: 'AUG' },
        { label: t('September'), value: 'SEP' },
        { label: t('October'), value: 'OCT' },
        { label: t('November'), value: 'NOV' },
        { label: t('December'), value: 'DEC' },
    ];

    const daysOfWeek = [
        { label: t('Monday'), value: 'MON' },
        { label: t('Tuesday'), value: 'TUE' },
        { label: t('Wednesday'), value: 'WED' },
        { label: t('Thursday'), value: 'THU' },
        { label: t('Friday'), value: 'FRI' },
        { label: t('Saturday'), value: 'SAT' },
        { label: t('Sunday'), value: 'SUN' },
    ];

    useEffect(() => {
        if (currentObjective) {
            setEnableApplicability(!!currentObjective.applicability);
            if (currentObjective.applicability) {
                // console.log('currentObjective.applicability:', currentObjective.applicability);
                const cronParts = currentObjective.applicability.split(' ');
                // console.log('cronParts:', cronParts);

                // Check if cronParts are '*', if so, set the corresponding state to an empty array
                setSelectedHours(cronParts[1] === '*' ? [] : cronParts[1].split(',').map(Number));
                setSelectedDays(cronParts[5] === '*' ? [] : cronParts[5].split(',').map(day => {
                    const dayObj = daysOfWeek.find(d => d.value === day);
                    return dayObj ? dayObj.value : day;
                }));
                setSelectedMonths(cronParts[4] === '*' ? [] : cronParts[4].split(',').map(month => {
                    const monthObj = months.find(m => m.value === month);
                    return monthObj ? monthObj.value : month;
                }));
                // console.log('selectedMonths:', selectedMonths);
                // console.log('selectedDays:', selectedDays);
            }
        }
    }, [currentObjective]);

    const generateCronExpression = () => {
        const monthsPart = selectedMonths.length ? selectedMonths.join(',') : '*';
        const daysPart = selectedDays.length ? selectedDays.join(',') : '*';
        const hoursPart = selectedHours.length ? selectedHours.join(',') : '*';
        return `* ${hoursPart} * ? ${monthsPart} ${daysPart}`;
    };

    useEffect(() => {
        if (enableApplicability) {
            const cronExpr = generateCronExpression();
            if (cronExpr !== cronExpression) {
                setCronExpression(cronExpr);
                setObjective(prevState => ({ ...prevState, applicability: cronExpr }));
            }
        } else {
            if (cronExpression !== null) {
                setCronExpression(null);
                setObjective(prevState => ({ ...prevState, applicability: null }));
            }
        }
    }, [enableApplicability, selectedMonths, selectedDays, selectedHours]);

    const handleToggle = (setSelected, selected, value) => {
        setSelected(prev => {
            const newSelected = prev.includes(value)
                ? prev.filter(v => v !== value)
                : [...prev, value];
            return newSelected.sort((a, b) => a - b);
        });
    };

    return (
        <div className={generateClassName('objective-applicability', '-container', className)}>
            <div className='objective-applicability-enabler'>
                <FieldComponent
                    label={t('Enable Applicability')}
                    labelAlign='center'
                    component={
                        <Checkbox
                            checked={enableApplicability}
                            onChange={() => setEnableApplicability(!enableApplicability)}
                            icon={<CheckBoxOutlineBlank />}
                            checkedIcon={<CheckBox />}
                            className='objective-applicability-checkbox'
                        />
                    }
                    className={'objective-applicability-enable'}
                />
            </div>
            {enableApplicability && (
                <>
                    <FieldComponent
                        label={t('Months Applicability')}
                        labelAlign='left'
                        component={
                            <SelectableList
                                items={months}
                                selectedItems={selectedMonths}
                                setSelectedItems={setSelectedMonths}
                                rows={2}
                                className={'objective-applicability-months-list'}
                            />
                        }
                        className={'objective-applicability-months'}
                    />
                    <FieldComponent
                        label={t('Days of the Week Applicability')}
                        labelAlign='left'
                        component={
                            <SelectableList
                                items={daysOfWeek}
                                selectedItems={selectedDays}
                                setSelectedItems={setSelectedDays}
                                rows={1}
                                className={'objective-applicability-days-list'}
                            />
                        }
                        className={'objective-applicability-days'}
                    />
                    <FieldComponent
                        label={t('Hours Applicability')}
                        labelAlign='left'
                        component={
                            <SelectableList
                                items={hours}
                                selectedItems={selectedHours}
                                setSelectedItems={setSelectedHours}
                                rows={2}
                                className={'objective-applicability-hours-list'}
                            />
                        }
                        className={'objective-applicability-hours'}
                    />
                </>
            )}
        </div >
    );
};

export default Applicability;